/**
 * DnsGuideModal — the COMPLETE copy-ready DNS setup guide for a hosting
 * account: step-by-step instructions plus every record (website A records
 * and the full mail suite: MX, SPF, DKIM, DMARC, webmail).
 *
 * Read-only by doctrine: 12and22 never edits DNS; the domain's controller
 * adds these at their DNS provider. Data from
 * GET /api/hosting/accounts/:id/dns-records.
 *
 * Used from both the hosting list (HostingDashboard) and the account
 * detail Overview (HostingAccountDetail).
 */
import React, { useEffect, useState } from 'react';

interface GuideRecord { type: string; name: string; value: string; ttl: number; purpose: string; required: boolean }
interface GuideData { domain: string; groups: { title: string; records: GuideRecord[] }[]; notes: string[] }

const DnsGuideModal: React.FC<{ hostingAccountId: string; onClose: () => void }> = ({ hostingAccountId, onClose }) => {
  const [guide, setGuide] = useState<GuideData | null>(null);
  const [err, setErr] = useState('');
  const [copiedIdx, setCopiedIdx] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/dns-records`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await resp.json();
        if (json.success) setGuide(json.data);
        else setErr(json.error || 'Failed to load record list');
      } catch (e: any) { setErr(e.message || 'Network error'); }
    })();
  }, [hostingAccountId]);

  const copyVal = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopiedIdx(key); setTimeout(() => setCopiedIdx(null), 1500); } catch { /* */ }
  };

  const copyAll = async () => {
    if (!guide) return;
    const lines = guide.groups.flatMap(g => [
      `# ${g.title}`,
      ...g.records.map(r => `${r.type}\t${r.name}\t${r.value}\tTTL ${r.ttl}${r.required ? '' : '\t(optional)'}`),
    ]);
    await copyVal('__all__', lines.join('\n'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 sticky top-0 bg-white flex items-start justify-between gap-3 z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">DNS setup{guide ? ` — ${guide.domain}` : ''}</h3>
            <p className="text-sm text-gray-500 mt-1">Everything needed for your website, email and webmail to work.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyAll} disabled={!guide}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:text-gray-900 disabled:opacity-50">
              {copiedIdx === '__all__' ? 'Copied ✓' : 'Copy all'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step-by-step instructions */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">How to point your domain — step by step</h4>
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-5">
              <li>
                Log in to <strong>whoever manages DNS for this domain</strong> — that's the registrar or DNS host
                where its nameservers live (e.g. AWS Route 53, GoDaddy, Namecheap, Cloudflare).
              </li>
              <li>
                Add <strong>every record in the tables below exactly as shown</strong> — same type, name and value.
                For the "Name / Host" column, <code className="bg-white px-1 rounded border border-slate-200">@</code> means
                the bare domain itself; some providers want it left empty instead.
              </li>
              <li>
                <strong>Website records (A)</strong> make your site load. <strong>Email records</strong> (MX, SPF, DKIM,
                DMARC) make sending and receiving mail work and keep it out of spam — add all of them, not just MX.
              </li>
              <li>
                Wait for DNS propagation — usually minutes, up to 48 hours. Verification is automatic: mail record
                status appears in the <strong>Mailboxes</strong> tab, and the website check in the <strong>SSL</strong> tab.
              </li>
              <li>
                Once the domain resolves here, open the <strong>SSL</strong> tab and click
                <strong> Issue free certificate</strong> — HTTPS goes live and renews automatically.
              </li>
            </ol>
            <p className="text-xs text-slate-500 mt-3">
              12and22 never edits DNS for you — pointing your domain is always your action, at your DNS provider.
            </p>
          </div>

          {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">{err}</div>}
          {!guide && !err && <div className="text-sm text-gray-500">Loading record list…</div>}

          {guide && guide.groups.map(group => (
            <div key={group.title} className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{group.title} records</h4>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Name / Host</th>
                      <th className="px-3 py-2">Value</th>
                      <th className="px-3 py-2">TTL</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.records.map((r, i) => {
                      const key = `${group.title}-${i}`;
                      return (
                        <tr key={key} className="border-t border-gray-100 align-top">
                          <td className="px-3 py-2 font-mono text-xs font-semibold text-gray-900 whitespace-nowrap">{r.type}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-800 break-all max-w-[180px]">{r.name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-800 break-all max-w-[280px]">
                            {r.value}
                            <div className="text-[10px] text-gray-400 font-sans mt-0.5">{r.purpose}{r.required ? '' : ' (optional)'}</div>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.ttl}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => copyVal(key, r.value)}
                                    className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">
                              {copiedIdx === key ? 'Copied ✓' : 'Copy'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {guide && guide.notes.length > 0 && (
            <ul className="text-xs text-gray-500 space-y-1.5 mt-2 list-disc pl-4">
              {guide.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DnsGuideModal;
